import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import BlogEditor from './BlogEditor';

const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchBlogs();
  }, [filterStatus]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('blogs')?.select('*')?.order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query?.eq('status', filterStatus);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setBlogs(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError(err?.message || 'Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (!confirm('Are you sure you want to delete this blog post?')) return;

    try {
      const { error: deleteError } = await supabase?.from('blogs')?.delete()?.eq('id', blogId);

      if (deleteError) throw deleteError;
      await fetchBlogs();
    } catch (err) {
      console.error('Error deleting blog:', err);
      alert('Failed to delete blog: ' + err?.message);
    }
  };

  const handleStatusChange = async (blogId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'published') {
        updateData.published_at = new Date()?.toISOString();
      }

      const { error: updateError } = await supabase?.from('blogs')?.update(updateData)?.eq('id', blogId);

      if (updateError) throw updateError;
      await fetchBlogs();
    } catch (err) {
      console.error('Error updating blog status:', err);
      alert('Failed to update status: ' + err?.message);
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingBlog(null);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingBlog(null);
    fetchBlogs();
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-success text-white';
      case 'draft':
        return 'bg-warning text-white';
      case 'archived':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (showEditor) {
    return <BlogEditor blog={editingBlog} onClose={handleEditorClose} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Blog Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create, edit, and manage blog posts
          </p>
        </div>
        <Button onClick={handleCreate} iconName="Plus" iconPosition="left">
          Create New Blog
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            filterStatus === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          All ({blogs?.length || 0})
        </button>
        <button
          onClick={() => setFilterStatus('published')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            filterStatus === 'published' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Published
        </button>
        <button
          onClick={() => setFilterStatus('draft')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            filterStatus === 'draft' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Drafts
        </button>
        <button
          onClick={() => setFilterStatus('archived')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            filterStatus === 'archived' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Archived
        </button>
      </div>
      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}
      {!loading && blogs?.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            {filterStatus === 'all' ?'No blog posts yet. Create your first one!'
              : `No ${filterStatus} blog posts found.`}
          </p>
        </div>
      )}
      {!loading && blogs?.length > 0 && (
        <div className="space-y-4">
          {blogs?.map((blog) => (
            <div
              key={blog?.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-smooth"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      {blog?.title}
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        getStatusBadgeColor(blog?.status)
                      }`}
                    >
                      {blog?.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {blog?.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      {blog?.author_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="FolderOpen" size={12} />
                      {blog?.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} />
                      {new Date(blog?.created_at)?.toLocaleDateString()}
                    </span>
                    {blog?.published_at && (
                      <span className="flex items-center gap-1">
                        <Icon name="CheckCircle" size={12} />
                        Published {new Date(blog?.published_at)?.toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(blog)}
                    iconName="Edit"
                  />
                  {blog?.status === 'draft' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(blog?.id, 'published')}
                      iconName="Send"
                    />
                  )}
                  {blog?.status === 'published' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(blog?.id, 'archived')}
                      iconName="Archive"
                    />
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(blog?.id)}
                    iconName="Trash2"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogManagement;